import React, {useMemo, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {Colors} from '../../constants/colors'
import {PayArticleTypeModel} from '../../redux/types'
import {
  ColumnDef,
  Header,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faEdit, faTrashAlt} from '@fortawesome/free-solid-svg-icons'

interface Props {
  payArticleTypes: PayArticleTypeModel[]
  onEditClick?: (payArticleType: PayArticleTypeModel) => void
  onDeleteClick?: (payArticleType: PayArticleTypeModel) => void
}

const PayArticleTypeList = ({payArticleTypes, onEditClick, onDeleteClick}: Props) => {
  const styles = useStyles()
  const [sorting, setSorting] = useState<SortingState>([{id: 'code', desc: false}])

  const columns = useMemo<ColumnDef<PayArticleTypeModel>[]>(() => [
    {
      id: 'code',
      header: 'Code',
      accessorFn: (row) => row.code,
      cell: ({row}) => <span>{row.original.code}</span>,
    },
    {
      id: 'displayName',
      header: 'Display name',
      accessorFn: (row) => row.displayName,
      cell: ({row}) => <span>{row.original.displayName}</span>,
    },
    {
      id: 'valueSign',
      header: 'Value sign',
      accessorFn: (row) => row.valueSign,
      cell: ({row}) => <span>{row.original.valueSign}</span>,
    },
    {
      id: 'accrualType',
      header: 'Accrual type',
      accessorFn: (row) => row.accrualType,
      cell: ({row}) => <span>{row.original.accrualType}</span>,
    },
    {
      id: 'availabilityRatio',
      header: 'Availability ratio',
      accessorFn: (row) => row.availabilityRatio,
      cell: ({row}) => <span>{row.original.availabilityRatio.toLocaleString('sv-SE')}</span>,
    },
    {
      id: 'isDefaultSalary',
      header: 'Default salary',
      accessorFn: (row) => row.isDefaultSalary,
      cell: ({row}) => <span>{row.original.isDefaultSalary ? 'yes' : 'no'}</span>,
    },
    {
      id: 'isDefaultStandardShift',
      header: 'Default standard shift',
      accessorFn: (row) => row.isDefaultStandardShift,
      cell: ({row}) => <span>{row.original.isDefaultStandardShift ? 'yes' : 'no'}</span>,
    },
    {
      id: 'isDefaultCustomShift',
      header: 'Default custom shift',
      accessorFn: (row) => row.isDefaultCustomShift,
      cell: ({row}) => <span>{row.original.isDefaultCustomShift ? 'yes' : 'no'}</span>,
    },
    {
      id: 'isDefaultPayDeviation',
      header: 'Default pay deviation',
      accessorFn: (row) => row.isDefaultPayDeviation,
      cell: ({row}) => <span>{row.original.isDefaultPayDeviation ? 'yes' : 'no'}</span>,
    },
    {
      id: 'isShiftBasedSalaryArticle',
      header: 'Shift-based salary article',
      accessorFn: (row) => row.isShiftBasedSalaryArticle,
      cell: ({row}) => <span>{row.original.isShiftBasedSalaryArticle ? 'yes' : 'no'}</span>,
    },
    {
      id: 'edit',
      header: 'Edit',
      cell: ({row}) => (
        <button
          type='button'
          className={styles.iconButton}
          onClick={() => onEditClick && onEditClick(row.original)}
        >
          <FontAwesomeIcon
            className={styles.button}
            icon={faEdit}
            color={Colors.euro_600}
            title='Edit'
          />
        </button>
      ),
      enableSorting: false,
    },
    {
      id: 'delete',
      header: 'Delete',
      cell: ({row}) => (
        <button
          type='button'
          className={styles.iconButton}
          onClick={() => onDeleteClick && onDeleteClick(row.original)}
        >
          <FontAwesomeIcon
            className={styles.button}
            icon={faTrashAlt}
            color={Colors.euro_600}
            title='Delete'
          />
        </button>
      ),
      enableSorting: false,
    }
  ], [onDeleteClick, onEditClick, styles.button, styles.iconButton])

  const table = useReactTable({
    data: payArticleTypes ?? [],
    columns,
    state: {sorting},
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const renderHeaderContent = (header: Header<PayArticleTypeModel, unknown>) => {
    if (header.isPlaceholder) return null

    const canSort = header.column.getCanSort()
    const sortDirection = header.column.getIsSorted()

    if (!canSort) {
      return flexRender(header.column.columnDef.header, header.getContext())
    }

    return (
      <button
        type='button'
        className={styles.headerButton}
        onClick={header.column.getToggleSortingHandler()}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        <span>
          {sortDirection === 'asc' ? ' ▲' : sortDirection === 'desc' ? ' ▼' : ''}
        </span>
      </button>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} className={styles.headerCell}>
                  {renderHeaderContent(header)}
                </th>
              ))}
            </tr>
          ))}
          </thead>
          <tbody>
          {table.getRowModel().rows.length > 0 && table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={styles.bodyRow}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={styles.bodyCell}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
          {table.getRowModel().rows.length === 0 && (
            <tr className={styles.emptyRow}>
              <td className={styles.emptyCell} colSpan={columns.length}>
                No pay article types configured
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default PayArticleTypeList

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '14px',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Bold'
  },
  headerButton: {
    all: 'unset',
    display: 'inline-flex',
    gap: 6,
    cursor: 'pointer',
  },
  bodyRow: {
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '6px 12px',
    textAlign: 'left',
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  emptyRow: {
    backgroundColor: Colors.darkGrayCard,
  },
  emptyCell: {
    padding: '12px',
    textAlign: 'center',
  },
  iconButton: {
    all: 'unset',
    cursor: 'pointer',
  },
  button: {
    transition: '0.4s',
    '&:hover': {
      color: Colors.euro5,
      cursor: 'pointer',
    }
  },
})
